import type {
  TaskCommentView,
  TaskExecutionSummary,
  TaskView,
} from '@/lib/api/generated/model';
import type { BoardStepSection } from './boardModel';
import {
  hasLiveExecution,
  isTerminalStatus,
  newestFirst,
} from './executionStatus';
import { outcomesFor, type Outcome } from './primaryAction';

/**
 * Which comments a step has not run with (spec 235), derived from what the drawer
 * already holds: the timeline and the task's runs. Nothing is stored.
 *
 * Every run reads the task's whole comment timeline as part of its instructions, so
 * a note does reach the agent; what was missing is the link between writing it and
 * the run that will read it. A comment is **new** when a person wrote it after the
 * latest run of the task's current step began — its start, or when it was asked
 * for while it waits its turn. A run's record exists from the moment a move or a
 * rerun asks for it, gates included, so every step that ran has one. On a step that has never run nothing is new: there is no "last run" for the
 * note to follow. The agent's own comments are never counted: its result note would
 * otherwise read as one new comment after every run.
 */
export function newCommentIds(
  comments: readonly TaskCommentView[],
  executions: readonly TaskExecutionSummary[],
  stepId: string | undefined,
  { running = false }: { running?: boolean } = {},
): ReadonlySet<string> {
  if (stepId === undefined) return new Set();
  const runs = newestFirst(
    executions.filter((execution) => execution.step_id_snapshot === stepId),
  );
  const [latest, previous] = runs;
  if (latest === undefined) return new Set();
  // While the latest run is in flight, the comments that matter are the ones it was
  // given: written between the run before it and its own start. A note written
  // during the run waits for the next one.
  const from = running ? startOf(previous) : startOf(latest);
  const to = running ? startOf(latest) : undefined;
  if (from === undefined) return new Set();
  return new Set(
    comments
      .filter(
        (entry) =>
          entry.actor_type === 'user' &&
          entry.created_at > from &&
          (to === undefined || entry.created_at <= to),
      )
      .map((entry) => entry.id),
  );
}

/**
 * When a run began reading: its start, or when it was asked for if it has not
 * started (queued). The service marks the agent's comments by the same rule
 * (spec 174), so the count a person sees and the notes the agent is told are new
 * agree.
 */
function startOf(run: TaskExecutionSummary | undefined): string | undefined {
  return run ? (run.started_at ?? run.created_at) : undefined;
}

/** What the Comments tab offers under the timeline once people have written since the last run. */
export type NewCommentsBlockState =
  | { kind: 'run-again'; count: number; stepLabel: string }
  | { kind: 'decide'; count: number; stepLabel: string; outcomes: Outcome[] }
  | { kind: 'running'; count: number; stepLabel: string };

/**
 * The block's state (spec 235 FR-003), or null when it has nothing to offer.
 *
 * A gate offers its own ways on and never Run again (spec 172): a run on a gate runs
 * no agent, and the way back is what carries the note to the step that does. Any
 * other step offers Run again exactly when its ⋯ menu would. Off a placed step, on
 * Backlog or Done, or when the last run was refused by a spend limit, there is
 * nothing to press that the service would accept.
 */
export function newCommentsBlock(
  task: TaskView,
  step: BoardStepSection | undefined,
  steps: readonly BoardStepSection[],
  comments: readonly TaskCommentView[],
): NewCommentsBlockState | null {
  if (step === undefined || step.systemRole !== undefined) return null;
  const executions = task.executions ?? [];
  const latestOfStep = newestFirst(
    executions.filter(
      (execution) => execution.step_id_snapshot === step.stepId,
    ),
  )[0];
  const running =
    hasLiveExecution(task) &&
    latestOfStep !== undefined &&
    !isTerminalStatus(latestOfStep.status);
  const count = newCommentIds(comments, executions, step.stepId, {
    running,
  }).size;
  if (count === 0) return null;
  if (running) return { kind: 'running', count, stepLabel: step.label };
  if (latestOfStep?.status === 'blocked' && latestOfStep.blocked_scope) {
    return null;
  }
  if (step.kind === 'human_gate') {
    const outcomes = outcomesFor(step, steps);
    return outcomes.length > 0
      ? { kind: 'decide', count, stepLabel: step.label, outcomes }
      : null;
  }
  return { kind: 'run-again', count, stepLabel: step.label };
}
